import React, { useRef, useLayoutEffect } from 'react';

type Props = React.TextareaHTMLAttributes<HTMLTextAreaElement>;

/**
 * A textarea that looks like a single-line field but wraps long text onto new
 * lines and grows its height to fit, instead of scrolling horizontally (which
 * hides earlier text behind the right edge while typing). Drop-in replacement
 * for the checklist <input> fields in TaskCreate / TaskEdit / the Checklist
 * component. Accepts all native textarea props (value, onChange, onKeyDown,
 * placeholder, className, style, …).
 */
export function AutoGrowTextarea({ value, className = "", rows = 1, ...rest }: Props) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const resize = () => {
    const el = ref.current;
    if (!el) return;
    // Reset first so the textarea can shrink when text is deleted.
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  // Resize on mount and whenever the value changes (typing or external edits).
  useLayoutEffect(() => {
    resize();
  }, [value]);

  return (
    <textarea
      ref={ref}
      rows={rows}
      value={value}
      onInput={resize}
      className={`resize-none overflow-hidden ${className}`}
      {...rest}
    />
  );
}

export default AutoGrowTextarea;
